import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Confirm } from "./Shared";
import { toast } from "sonner";
import { Heading, Plus, Settings2, Trash2, Save } from "lucide-react";

const KEY = ["estimate-section-titles"];
const useTitles = () => useQuery({ queryKey: KEY, queryFn: () => api.get("/estimate-section-titles").then((r) => r.data) });
const errMsg = (e, d) => formatApiErrorDetail(e.response?.data?.detail) || d;

export const SectionPicker = ({ onPick }) => {
  const qc = useQueryClient();
  const { data: titles = [] } = useTitles();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const q = search.trim();
  const exact = titles.some((t) => t.name.toLowerCase() === q.toLowerCase());
  const pick = (name) => { onPick(name); setOpen(false); setSearch(""); };
  const createAndPick = async () => {
    try {
      await api.post("/estimate-section-titles", { name: q });
      qc.invalidateQueries({ queryKey: KEY });
      toast.success("Title saved to the list."); pick(q);
    } catch (e) { toast.error(errMsg(e, "Could not save title.")); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid="estimate-add-section"><Heading className="h-4 w-4 mr-2" /> Add section title</Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search or type a title…" value={search} onValueChange={setSearch} data-testid="estimate-section-search" />
          <CommandList className="max-h-64">
            <CommandEmpty>No saved title found.</CommandEmpty>
            {titles.map((t) => (
              <CommandItem key={t.id} value={t.name} onSelect={() => pick(t.name)} data-testid={`estimate-section-option-${t.id}`}>{t.name}</CommandItem>
            ))}
          </CommandList>
          {q && !exact && (
            <button onClick={createAndPick} data-testid="estimate-section-create" className="w-full border-t border-slate-100 px-3 py-2 text-left text-sm text-[#1E3A8A] hover:bg-slate-50 flex items-center gap-2">
              <Plus className="h-4 w-4" /> Add "{q}" to the list
            </button>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const TitleRow = ({ t, onSaved }) => {
  const [name, setName] = useState(t.name);
  const save = async () => {
    try { await api.put(`/estimate-section-titles/${t.id}`, { name }); toast.success("Title updated."); onSaved(); }
    catch (e) { toast.error(errMsg(e, "Could not update title.")); }
  };
  const del = async () => {
    try { await api.delete(`/estimate-section-titles/${t.id}`); toast.success("Title removed."); onSaved(); }
    catch (e) { toast.error(errMsg(e, "Could not remove title.")); }
  };
  return (
    <div className="flex items-center gap-2" data-testid={`section-title-row-${t.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`section-title-name-${t.id}`} />
      <button onClick={save} disabled={name.trim() === t.name || !name.trim()} title="Save" data-testid={`section-title-save-${t.id}`} className="p-1 text-[#1E3A8A] disabled:text-slate-300"><Save className="h-4 w-4" /></button>
      <Confirm onConfirm={del} title="Remove title?" description={`"${t.name}" will be removed from the list. Existing estimates are not affected.`}
        trigger={<button title="Remove" data-testid={`section-title-delete-${t.id}`} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>} />
    </div>
  );
};

export const ManageSectionTitles = () => {
  const qc = useQueryClient();
  const { data: titles = [] } = useTitles();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const add = async () => {
    if (!name.trim()) { toast.error("Enter a title."); return; }
    try { await api.post("/estimate-section-titles", { name }); setName(""); toast.success("Title added."); refresh(); }
    catch (e) { toast.error(errMsg(e, "Could not add title.")); }
  };
  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)} data-testid="estimate-manage-sections" className="text-slate-600"><Settings2 className="h-4 w-4 mr-2" /> Manage titles</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="manage-sections-dialog">
          <DialogHeader><DialogTitle>Section titles</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Short titles you can insert between estimate items, e.g. Cabling Work.</p>
          <div className="flex items-center gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="New title" data-testid="section-title-new" />
            <Button size="sm" onClick={add} data-testid="section-title-add-btn" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
            {titles.map((t) => <TitleRow key={`${t.id}-${t.name}`} t={t} onSaved={refresh} />)}
            {!titles.length && <p className="text-sm text-slate-400">No titles yet.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
